import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, ChevronRight, CheckCircle, BarChart3, GraduationCap, 
  MessageSquare, AlertTriangle, Target, Users, Building2, Mail,
  Phone, ArrowRight, Star, TrendingUp, Layers, Sparkles, Map,
  FileText, Clock, Zap, Brain, Eye, Lock, Download
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ERICLogo({ size = 'default' }) {
  const sizes = {
    small: { text: 'text-2xl', sub: 'text-xs' },
    default: { text: 'text-4xl', sub: 'text-sm' },
    large: { text: 'text-6xl', sub: 'text-lg' }
  };
  const s = sizes[size];
  return (
    <div className="flex flex-col items-center">
      <div className={`font-outfit font-bold ${s.text} tracking-tight`}>
        <span className="text-emerald-500">E</span>
        <span className="text-blue-600">R</span>
        <span className="text-blue-600">I</span>
        <span className="text-orange-500">C</span>
      </div>
      <div className={`${s.sub} text-slate-600 font-medium tracking-wide`}>
        Education Risk Intelligence Center
      </div>
    </div>
  );
}

function DemoRequestModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', district: '', role: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/demo-request`, formData);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in">
        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-6">We've received your demo request and will be in touch within 24 hours.</p>
            <button
              onClick={() => { onClose(); setSubmitted(false); setFormData({ name: '', email: '', phone: '', district: '', role: '', message: '' }); }}
              className="bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-all"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-outfit text-xl font-bold text-slate-900">Request a Demo</h2>
                  <p className="text-slate-600 text-sm">See how ERIC can transform your district's risk intelligence</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-all">
                  <span className="text-2xl text-slate-400">&times;</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="John Smith" data-testid="demo-name" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Role/Title</label>
                  <input type="text" name="role" value={formData.role} onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Superintendent" data-testid="demo-role" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="jsmith@district.edu" data-testid="demo-email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="(555) 123-4567" data-testid="demo-phone" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">District Name *</label>
                  <input type="text" name="district" value={formData.district} onChange={handleChange} required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Springfield ISD" data-testid="demo-district" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">What are your key challenges?</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Tell us about your district's risk management needs..." data-testid="demo-message" />
              </div>
              <button type="submit" disabled={submitting}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2" data-testid="demo-submit">
                {submitting ? 'Submitting...' : 'Request Demo'}
                {!submitting && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [showDemoModal, setShowDemoModal] = useState(false);

  const skills = [
    'Define Reality', 'Fine Tune Systems', 'Boost Confidence', 'Balance Exposures',
    'Perpetuate Ownership', 'Set Perimeter', 'Scrub Data', 'Place Sensors',
    'Clock Movements', 'Monitor Disruptors'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <ERICLogo size="small" />
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-login">
              Sign In
            </Link>
            <button onClick={() => setShowDemoModal(true)}
              className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center gap-2" data-testid="nav-demo-btn">
              Request Demo <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section — Lead with the problem */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">AI-Powered K-12 Risk Intelligence</span>
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Stop Managing Risk by Spreadsheet.<br />
              <span className="text-blue-400">Start Managing It by Intelligence.</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-2xl">
              Most districts don't know their true risk posture until something goes wrong. 
              ERIC uses AI to assess 136 risk items across 15 categories in a single conversation — 
              replacing months of manual audits with a 30-minute intelligent assessment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => setShowDemoModal(true)}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 text-lg" data-testid="hero-demo-btn">
                Request a Demo <ArrowRight className="w-5 h-5" />
              </button>
              <a href="/ERIC_Executive_Overview.pdf" target="_blank" rel="noopener noreferrer"
                className="bg-white/10 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all flex items-center justify-center gap-2 text-lg border border-white/20">
                <Download className="w-5 h-5" /> Download Overview
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Why ERIC — Pain-point driven */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-3">The Problem</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Your District Has Blind Spots. ERIC Finds Them.
            </h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">
              School districts face more risk categories than ever — yet most still rely on fragmented 
              processes, tribal knowledge, and reactive responses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="relative bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-5">
                <Eye className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">
                Invisible Gaps
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                136 distinct risk items exist across a typical district. Without a structured framework, 
                critical gaps go undetected — until they become incidents, lawsuits, or front-page headlines.
              </p>
            </div>

            <div className="relative bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-5">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">
                Manual Overload
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Traditional risk assessments take weeks of staff time, produce inconsistent results, 
                and sit in binders gathering dust. By the time you finish, conditions have already changed.
              </p>
            </div>

            <div className="relative bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-5">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">
                No Clear Path Forward
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Even when risks are identified, most districts lack a prioritized improvement plan. 
                Without cross-category analysis, you fix symptoms instead of root causes — and 
                the same problems return.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — 3 steps */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-600 mb-3">How It Works</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              From Assessment to Action in Three Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              {
                step: '01',
                icon: Sparkles,
                title: 'AI Assessment',
                desc: 'Have a guided conversation with our AI. In 30 minutes, it evaluates your district across 15 categories and scores 136 individual risk items — no forms, no spreadsheets.',
                color: 'text-blue-600',
                bg: 'bg-blue-50',
                iconBg: 'bg-blue-600',
              },
              {
                step: '02',
                icon: Layers,
                title: 'Balanced Scorecard',
                desc: 'See your complete risk posture quantified: overall score, the 3Rs (Readiness, Reliability, Resilience), and drill down into every category. Manually adjust any score the AI got wrong.',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50',
                iconBg: 'bg-emerald-600',
              },
              {
                step: '03',
                icon: Map,
                title: 'Improvement Roadmap',
                desc: 'AI analyzes cross-category dependencies and generates a phased action plan: Quick Wins, Core Improvements, and Advanced Excellence — with specific, trackable recommendations.',
                color: 'text-orange-600',
                bg: 'bg-orange-50',
                iconBg: 'bg-orange-500',
              },
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className={`${item.bg} rounded-2xl p-8 mx-2 h-full border border-slate-200`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-xl ${item.iconBg} flex items-center justify-center`}>
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className={`font-outfit text-3xl font-bold ${item.color} opacity-30`}>{item.step}</span>
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:flex absolute top-1/2 -right-3 z-10 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Differentiator */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-200 mb-3">AI-Powered Intelligence</p>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6 leading-tight">
                What Used to Take Weeks<br />Now Takes 30 Minutes
              </h2>
              <p className="text-blue-100 text-base leading-relaxed mb-8">
                ERIC's AI doesn't just collect data — it understands context. Through a natural 
                conversation about your operations, it infers scores across all 136 risk items, 
                identifies hidden dependencies between categories, and generates prioritized 
                recommendations tailored to your district.
              </p>
              <div className="space-y-4">
                {[
                  'Conversational assessment — answer questions, not forms',
                  'AI scores 136 items with confidence ratings you can verify',
                  'Cross-category dependency analysis humans typically miss',
                  'Board-ready PDF report generated instantly',
                  'Always-available AI advisor for ongoing guidance',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-blue-50 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-blue-200 text-sm font-medium">Traditional Audit</span>
                  <span className="text-blue-200 text-sm font-medium">ERIC AI</span>
                </div>
                {[
                  { label: 'Assessment Time', old: '4-6 weeks', now: '30 minutes' },
                  { label: 'Items Evaluated', old: '20-30 items', now: '136 items' },
                  { label: 'Categories', old: '5-8 categories', now: '15 categories' },
                  { label: 'Improvement Plan', old: 'Generic list', now: 'AI-prioritized roadmap' },
                  { label: 'Progress Tracking', old: 'Manual follow-up', now: 'Real-time dashboard' },
                ].map((row, idx) => (
                  <div key={idx} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                    <div className="flex-1">
                      <p className="text-xs text-blue-300 mb-0.5">{row.label}</p>
                      <p className="text-white/50 text-sm line-through">{row.old}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold text-sm">{row.now}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features — Outcome focused */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-3">The Platform</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need. Nothing You Don't.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: 'AI Conversational Assessment', desc: 'A guided AI dialogue evaluates 136 risk items across 15 categories. No tedious forms — just answer questions about your district.', color: 'bg-blue-600' },
              { icon: Layers, title: 'Balanced Scorecard', desc: 'See your complete risk posture in one view. 4 sections, 15 categories, with the ability to manually adjust any AI-inferred score.', color: 'bg-emerald-600' },
              { icon: Map, title: 'Improvement Roadmap', desc: 'AI analyzes dependencies and builds a phased plan: Quick Wins, Core Improvements, Advanced Excellence — with trackable progress.', color: 'bg-orange-500' },
              { icon: BarChart3, title: '3Rs Dashboard', desc: 'Track Readiness, Reliability, and Resilience in real time. Scores pull directly from your Balanced Scorecard — no manual updates.', color: 'bg-purple-600' },
              { icon: GraduationCap, title: '10 Skills Training', desc: 'Comprehensive courses with K-12 case studies, exercises, and key takeaways. Training is tied directly to your weakest scorecard areas.', color: 'bg-amber-500' },
              { icon: MessageSquare, title: 'AI Risk Advisor', desc: 'An always-available chatbot trained on K-12 risk management. Ask about compliance, best practices, or your specific challenges.', color: 'bg-slate-700' },
            ].map((f, idx) => (
              <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-sm transition-all">
                <div className={`w-11 h-11 rounded-lg ${f.color} flex items-center justify-center mb-4`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10 Skills Framework — Credibility through methodology */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-4">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-400 mb-3">Research-Backed Framework</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              Built on the 10 Skills of School Risk Intelligence
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto mb-10">
              ERIC isn't guesswork. Every assessment, training module, and recommendation is grounded 
              in the proven 10 Skills framework — a comprehensive methodology for building 
              risk-intelligent school districts.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {skills.map((skill, index) => (
              <div key={index}
                className="bg-white/5 px-4 py-3.5 rounded-xl border border-white/10 hover:bg-white/10 transition-all text-center">
                <span className="text-blue-400 font-outfit font-bold text-lg block mb-0.5">{index + 1}</span>
                <span className="text-sm font-medium text-slate-300">{skill}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-slate-500 text-sm">
              Each skill includes 3-5 in-depth lessons with case studies, practical exercises, and measurable outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl font-outfit font-bold text-slate-900">136</p>
              <p className="text-sm text-slate-500 mt-1">Risk Items Evaluated</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-outfit font-bold text-blue-600">15</p>
              <p className="text-sm text-slate-500 mt-1">Assessment Categories</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-outfit font-bold text-emerald-600">10</p>
              <p className="text-sm text-slate-500 mt-1">Training Modules</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-outfit font-bold text-orange-500">30</p>
              <p className="text-sm text-slate-500 mt-1">Minute AI Assessment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Know Your Risk Posture Before It Knows You
          </h2>
          <p className="text-base text-slate-500 mb-10 max-w-xl mx-auto">
            The districts that thrive aren't the ones that avoid risk — they're the ones that understand it. 
            See where you stand in 30 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setShowDemoModal(true)}
              className="bg-blue-600 text-white px-10 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all inline-flex items-center justify-center gap-2 text-lg" data-testid="cta-demo-btn">
              Request Your Demo <ArrowRight className="w-5 h-5" />
            </button>
            <a href="/ERIC_Executive_Overview.pdf" target="_blank" rel="noopener noreferrer"
              className="bg-white text-slate-700 px-10 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all inline-flex items-center justify-center gap-2 text-lg border border-slate-200">
              <FileText className="w-5 h-5" /> View Sample Report
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <ERICLogo size="small" />
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-500">&copy; 2026 ERIC. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <DemoRequestModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
}
